import { SITE_URL } from "@/lib/constants";

export const locales = ["en", "ar", "zh", "tr", "de", "es", "fr", "pt", "ru"] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/** URL path prefix for non-default locales (English has no prefix). */
export const localePathPrefix: Record<Exclude<Locale, "en">, string> = {
  ar: "ar",
  zh: "zh",
  tr: "tr",
  de: "de",
  es: "es",
  fr: "fr",
  pt: "pt",
  ru: "ru",
};

/** Locales that use right-to-left layout. All others stay LTR. */
export const rtlLocales: readonly Locale[] = ["ar"];

export type TextDirection = "ltr" | "rtl";

export function getTextDirection(locale: Locale): TextDirection {
  return rtlLocales.includes(locale) ? "rtl" : "ltr";
}

export function isRtlLocale(locale: Locale): boolean {
  return getTextDirection(locale) === "rtl";
}

export function getLocalePathPrefix(locale: Locale): string | null {
  if (locale === defaultLocale) {
    return null;
  }

  return localePathPrefix[locale as Exclude<Locale, "en">];
}

export function getLocalizedPath(path: string, locale: Locale): string {
  const prefix = getLocalePathPrefix(locale);

  if (!prefix) {
    return path;
  }

  return path === "/" ? `/${prefix}` : `/${prefix}${path}`;
}

export function getOpenGraphLocale(locale: Locale): string {
  const map: Record<Locale, string> = {
    en: "en_GB",
    ar: "ar_SA",
    zh: "zh_CN",
    tr: "tr_TR",
    de: "de_DE",
    es: "es_ES",
    fr: "fr_FR",
    pt: "pt_BR",
    ru: "ru_RU",
  };

  return map[locale];
}

export function getMetadataLanguageAlternates(path: string) {
  const localizedUrls = Object.fromEntries(
    locales.map((locale) => [
      locale,
      `${SITE_URL}${getLocalizedPath(path, locale)}`,
    ]),
  ) as Record<Locale, string>;

  return {
    ...localizedUrls,
    "x-default": localizedUrls[defaultLocale],
  };
}

/**
 * @deprecated Prefer `isBlogLocaleEnabled()` from `@/lib/server/blog/locales`.
 * Kept as a sync allowlist of locales that may have blog routes.
 */
export const blogRouteLocales: readonly Locale[] = locales;

export function isBlogPath(path: string): boolean {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized === "/blog" || normalized.startsWith("/blog/");
}

/**
 * Localized blog URL path. Uses the same prefix rules as marketing pages.
 * Access for locales without published posts is gated in the blog layout.
 */
export function getBlogLocalizedPath(path: string, locale: Locale): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return getLocalizedPath(normalized, locale);
}

/**
 * DB `greip_blog.lang` value for a site locale.
 * Falls back to English when the locale is unknown.
 */
export function getBlogDataLocale(locale: Locale): Locale {
  return locale;
}

export const siteLanguages: Array<{ locale: Locale; label: string }> = [
  { locale: "en", label: "🇺🇸 English" },
  { locale: "ar", label: "🇸🇦 العربية" },
  { locale: "zh", label: "🇨🇳 简体中文" },
  { locale: "tr", label: "🇹🇷 Türkçe" },
  { locale: "de", label: "🇩🇪 Deutsch" },
  { locale: "es", label: "🇪🇸 Español" },
  { locale: "fr", label: "🇫🇷 Français" },
  { locale: "pt", label: "🇧🇷 Português" },
  { locale: "ru", label: "🇷🇺 Русский" },
];
